import * as React from 'react';
import {
  AgnesAlert,
  AgnesButton,
  AgnesLabel,
  AgnesPopover,
  AgnesPopoverTrigger,
  AgnesPopoverContent,
  AgnesSelect,
  AgnesSelectContent,
  AgnesSelectItem,
  AgnesSelectTrigger,
  AgnesSelectValue,
} from '@agnes/ui';
import { Filter, Trash2, X } from 'lucide-react';

import { GRAUS_RISCO } from '@/constants/ConstantGrauRiscoRubrica';
import { protegerSelectDoFechamento } from '@/lib/filtros';
import ServiceRubricaLote, {
  type RubricaLoteFilters,
  type RubricFilterOptions,
  type RubricGroupCard,
} from '@/services/ServiceRubricaLote';

import FilterChip from './FilterChip';
import FilterMultiSelect from './FilterMultiSelect';
import { type AppliedFilters, EMPTY_FILTERS } from '@/components/rubricas/rubricaLoteFilters';

function filterCount(filters: AppliedFilters) {
  return Object.values(filters).reduce<number>(
    (count, value) => count + (Array.isArray(value) ? value.length : Number(value != null)),
    0,
  );
}

function toggleValue<T>(values: T[], value: T): T[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

export default function FilterRubricaAnaliseLoteRegistros({
  dataQa,
  filters,
  onApply,
  grupo,
  baseFilters,
  refreshToken = 0,
  titulo,
}: {
  dataQa: string;
  filters: AppliedFilters;
  onApply: (filters: AppliedFilters) => void;
  grupo?: RubricGroupCard['grupo'];
  baseFilters?: RubricaLoteFilters;
  refreshToken?: number;
  titulo?: string;
}) {
  const internal = grupo != null;
  const [open, setOpen] = React.useState(false);
  const titleId = React.useId();
  const activeFilters = filterCount(filters);
  const [draft, setDraft] = React.useState(filters);
  const [options, setOptions] = React.useState<RubricFilterOptions | null>(null);
  const [error, setError] = React.useState(false);
  const [retry, setRetry] = React.useState(0);

  React.useEffect(() => {
    if (open) {
      setDraft(filters);
    }
  }, [filters, open]);

  React.useEffect(() => {
    if (!open) return;
    let ignore = false;
    const controller = new AbortController();
    setOptions(null);
    setError(false);
    ServiceRubricaLote.consultarFiltros(grupo ? { grupo, baseFilters } : {}, controller.signal)
      .then(({ data }) => {
        if (!ignore) setOptions(data);
      })
      .catch(() => {
        if (!ignore) setError(true);
      });
    return () => {
      ignore = true;
      controller.abort();
    };
  }, [baseFilters, grupo, open, refreshToken, retry]);

  return (
    <AgnesPopover open={open} onOpenChange={setOpen} modal={false}>
      <AgnesPopoverTrigger asChild>
        <AgnesButton
          data-qa={`${dataQa}-trigger`}
          variant="outlined"
          startIcon={<Filter data-qa={`${dataQa}-icone-filtro`} className="h-4 w-4" />}
        >
          {internal ? 'Filtros dos itens' : 'Filtros'}
          {activeFilters ? ` (${activeFilters})` : ''}
        </AgnesButton>
      </AgnesPopoverTrigger>
      <AgnesPopoverContent
        align="end"
        sideOffset={8}
        collisionPadding={12}
        className="flex max-h-[var(--radix-popover-content-available-height)] w-80 max-w-[calc(100vw-24px)] flex-col p-0"
        aria-labelledby={titleId}
        data-qa={`${dataQa}-popover`}
        onInteractOutside={protegerSelectDoFechamento}
      >
        <div
          data-qa={`${dataQa}-cabecalho`}
          className="flex shrink-0 items-center justify-between gap-3 border-b border-agnes-border px-4 py-3"
        >
          <span
            data-qa={`${dataQa}-titulo`}
            id={titleId}
            className="text-base font-semibold text-agnes-text"
          >
            {internal ? 'Filtros dos itens do grupo' : (titulo ?? 'Filtros da análise em lote')}
          </span>
          <button
            data-qa={`${dataQa}-fechar`}
            type="button"
            aria-label="Fechar filtros"
            onClick={() => setOpen(false)}
            className="shrink-0 text-agnes-text-muted hover:text-agnes-text"
          >
            <X data-qa={`${dataQa}-fechar-icone`} className="h-5 w-5" />
          </button>
        </div>
        <div
          data-qa={`${dataQa}-campos`}
          className="min-h-0 max-h-[60vh] space-y-5 overflow-y-auto px-4 py-4"
        >
          {error ? (
            <AgnesAlert data-qa={`${dataQa}-erro`} preset="error">
              Não foi possível carregar as opções dos filtros.
              <AgnesButton
                data-qa={`${dataQa}-tentar-novamente`}
                variant="text"
                onClick={() => setRetry((value) => value + 1)}
              >
                Tentar novamente
              </AgnesButton>
            </AgnesAlert>
          ) : !options ? (
            <p
              data-qa={`${dataQa}-carregando`}
              role="status"
              className="text-sm text-agnes-text-muted"
            >
              Carregando opções dos filtros...
            </p>
          ) : (
            <>
              {internal && (
                <div data-qa={`${dataQa}-campo-statusAnalise`}>
                  <p
                    data-qa={`${dataQa}-campo-statusAnalise-aviso-global`}
                    className="mb-2 text-sm text-agnes-text-muted"
                  >
                    Os filtros globais continuam aplicados.
                  </p>
                  <p
                    data-qa={`${dataQa}-campo-statusAnalise-label`}
                    className="mb-2 text-sm font-medium text-agnes-text"
                  >
                    Status da análise
                  </p>
                  <div
                    data-qa={`${dataQa}-campo-statusAnalise-opcoes`}
                    className="flex flex-wrap gap-2"
                  >
                    {options.statusAnalise.map((status) => (
                      <FilterChip
                        key={status.value}
                        data-qa={`${dataQa}-campo-statusAnalise-opcao-${encodeURIComponent(status.value)}`}
                        label={status.label}
                        active={draft.statuses.includes(status.value)}
                        onClick={() =>
                          setDraft((current) => ({
                            ...current,
                            statuses: toggleValue(current.statuses, status.value),
                          }))
                        }
                      />
                    ))}
                  </div>
                </div>
              )}
              <div data-qa={`${dataQa}-campo-grauRisco`}>
                <p
                  data-qa={`${dataQa}-campo-grauRisco-label`}
                  className="mb-2 text-sm font-medium text-agnes-text"
                >
                  Grau de risco
                </p>
                <div data-qa={`${dataQa}-campo-grauRisco-opcoes`} className="flex flex-wrap gap-2">
                  {options.grauRisco.map((risk) => (
                    <FilterChip
                      key={risk.value}
                      data-qa={`${dataQa}-campo-grauRisco-opcao-${encodeURIComponent(risk.value)}`}
                      label={risk.label}
                      color={GRAUS_RISCO.find((entry) => entry.value === risk.value)?.color}
                      active={draft.risks.includes(risk.value)}
                      onClick={() =>
                        setDraft((current) => ({
                          ...current,
                          risks: toggleValue(current.risks, risk.value),
                        }))
                      }
                    />
                  ))}
                </div>
              </div>
              <FilterMultiSelect
                label="Tipo"
                data-qa={`${dataQa}-campo-tipo`}
                options={options.tipo}
                values={draft.types}
                onToggle={(value) =>
                  setDraft((current) => ({ ...current, types: toggleValue(current.types, value) }))
                }
              />
              <div data-qa={`${dataQa}-campo-situacao`}>
                <p
                  data-qa={`${dataQa}-campo-situacao-label`}
                  className="mb-2 text-sm font-medium text-agnes-text"
                >
                  Situação
                </p>
                <div data-qa={`${dataQa}-campo-situacao-opcoes`} className="flex flex-wrap gap-2">
                  {options.situacao.map((situation) => (
                    <FilterChip
                      key={situation.value}
                      data-qa={`${dataQa}-campo-situacao-opcao-${encodeURIComponent(situation.value)}`}
                      label={situation.label}
                      active={draft.situations.includes(situation.value)}
                      onClick={() =>
                        setDraft((current) => ({
                          ...current,
                          situations: toggleValue(current.situations, situation.value),
                        }))
                      }
                    />
                  ))}
                </div>
              </div>
              <FilterMultiSelect
                label="Ocorrências"
                data-qa={`${dataQa}-campo-ocorrencia`}
                options={options.ocorrencia}
                values={draft.occurrences}
                onToggle={(value) =>
                  setDraft((current) => ({
                    ...current,
                    occurrences: toggleValue(current.occurrences, value),
                  }))
                }
              />
              <FilterMultiSelect
                label="CNPJs dos empregadores"
                data-qa={`${dataQa}-campo-cnpjEmpregador`}
                options={options.cnpjEmpregador}
                values={draft.employerCnpjs}
                onToggle={(value) =>
                  setDraft((current) => ({
                    ...current,
                    employerCnpjs: toggleValue(current.employerCnpjs, value),
                  }))
                }
              />
              <FilterMultiSelect
                label="IDs de tabela"
                data-qa={`${dataQa}-campo-idTabela`}
                options={options.idTabela}
                values={draft.tableIds}
                onToggle={(value) =>
                  setDraft((current) => ({
                    ...current,
                    tableIds: toggleValue(current.tableIds, value),
                  }))
                }
              />
              <div data-qa={`${dataQa}-campos-booleanos`} className="grid grid-cols-2 gap-3">
                <div data-qa={`${dataQa}-campo-excecao`}>
                  <AgnesLabel data-qa={`${dataQa}-campo-excecao-label`}>Exceção</AgnesLabel>
                  <AgnesSelect
                    value={draft.exception == null ? 'todos' : String(draft.exception)}
                    onValueChange={(value) =>
                      setDraft((current) => ({
                        ...current,
                        exception: value === 'todos' ? null : value === 'true',
                      }))
                    }
                  >
                    <AgnesSelectTrigger
                      data-qa={`${dataQa}-campo-excecao-trigger`}
                      aria-label="Exceção"
                    >
                      <AgnesSelectValue data-qa={`${dataQa}-campo-excecao-valor`} />
                    </AgnesSelectTrigger>
                    <AgnesSelectContent data-qa={`${dataQa}-campo-excecao-popover`}>
                      <AgnesSelectItem
                        data-qa={`${dataQa}-campo-excecao-opcao-todos`}
                        value="todos"
                      >
                        Todos
                      </AgnesSelectItem>
                      {options.excecao.map((option) => (
                        <AgnesSelectItem
                          data-qa={`${dataQa}-campo-excecao-opcao-${encodeURIComponent(option.value)}`}
                          key={String(option.value)}
                          value={String(option.value)}
                        >
                          {option.label}
                        </AgnesSelectItem>
                      ))}
                    </AgnesSelectContent>
                  </AgnesSelect>
                </div>
                <div data-qa={`${dataQa}-campo-usoEmFolha`}>
                  <AgnesLabel data-qa={`${dataQa}-campo-usoEmFolha-label`}>Uso em folha</AgnesLabel>
                  <AgnesSelect
                    value={draft.payrollUsage == null ? 'todos' : String(draft.payrollUsage)}
                    onValueChange={(value) =>
                      setDraft((current) => ({
                        ...current,
                        payrollUsage: value === 'todos' ? null : value === 'true',
                      }))
                    }
                  >
                    <AgnesSelectTrigger
                      data-qa={`${dataQa}-campo-usoEmFolha-trigger`}
                      aria-label="Uso em folha"
                    >
                      <AgnesSelectValue data-qa={`${dataQa}-campo-usoEmFolha-valor`} />
                    </AgnesSelectTrigger>
                    <AgnesSelectContent data-qa={`${dataQa}-campo-usoEmFolha-popover`}>
                      <AgnesSelectItem
                        data-qa={`${dataQa}-campo-usoEmFolha-opcao-todos`}
                        value="todos"
                      >
                        Todos
                      </AgnesSelectItem>
                      {options.usoEmFolha.map((option) => (
                        <AgnesSelectItem
                          data-qa={`${dataQa}-campo-usoEmFolha-opcao-${encodeURIComponent(option.value)}`}
                          key={String(option.value)}
                          value={String(option.value)}
                        >
                          {option.label}
                        </AgnesSelectItem>
                      ))}
                    </AgnesSelectContent>
                  </AgnesSelect>
                </div>
              </div>
              <FilterMultiSelect
                label="Natureza"
                data-qa={`${dataQa}-campo-natureza`}
                options={options.natureza}
                values={draft.natures}
                onToggle={(value) =>
                  setDraft((current) => ({
                    ...current,
                    natures: toggleValue(current.natures, value),
                  }))
                }
              />
            </>
          )}
        </div>
        <div
          data-qa={`${dataQa}-acoes`}
          className="flex shrink-0 flex-col gap-2 border-t border-agnes-border px-4 py-3"
        >
          <AgnesButton
            data-qa={`${dataQa}-aplicar`}
            variant="primary"
            className="w-full"
            disabled={!options || error}
            onClick={() => {
              onApply(draft);
              setOpen(false);
            }}
          >
            Aplicar filtro
          </AgnesButton>
          <AgnesButton
            data-qa={`${dataQa}-limpar`}
            variant="outlined"
            className="w-full"
            startIcon={<Trash2 data-qa={`${dataQa}-limpar-icone`} className="h-4 w-4" />}
            onClick={() => {
              setDraft(EMPTY_FILTERS);
              onApply(EMPTY_FILTERS);
              setOpen(false);
            }}
          >
            Limpar filtro
          </AgnesButton>
        </div>
      </AgnesPopoverContent>
    </AgnesPopover>
  );
}
